import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TimezoneSelector from './TimezoneSelector';

interface LovedOneData {
  id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  timezone: string;
  check_in_frequency: number;
  check_in_times: string[];
  custom_message: string;
  // Store full customer data for update
  caregiver_name?: string;
  caregiver_phone?: string;
  caregiver_email?: string;
}

const EditLovedOne: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [lovedOne, setLovedOne] = useState<LovedOneData>({
    id: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    timezone: 'America/Los_Angeles',
    check_in_frequency: 1,
    check_in_times: ['09:00'],
    custom_message: '',
  });

  // Load existing loved one data
  useEffect(() => {
    const loadLovedOneData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Fetch customers list (will be filtered by role - end users get assigned customers, admins get all)
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/customers`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const customers = await response.json();
          if (customers && Array.isArray(customers) && customers.length > 0) {
            // Get the first customer (for end users, this will be their assigned customer)
            const customer = customers[0];
            
            // Parse name into first and last name
            const nameParts = (customer.name || '').split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            
            // Get schedule information if available
            let checkInTimes = ['09:00'];
            let checkInFrequency = 1;
            if (customer.schedule && customer.schedule.check_in_times) {
              checkInTimes = customer.schedule.check_in_times.map((ct: any) => ct.time || '09:00');
              checkInFrequency = customer.schedule.check_in_times.length > 1 ? 2 : 1;
            }
            
            setLovedOne({
              id: customer.id?.toString() || '',
              first_name: firstName,
              last_name: lastName,
              phone_number: customer.phone_number || '',
              timezone: customer.timezone || 'America/Los_Angeles',
              check_in_frequency: checkInFrequency,
              check_in_times: checkInTimes,
              custom_message: customer.custom_message || '',
              caregiver_name: customer.caregiver_name || '',
              caregiver_phone: customer.caregiver_phone || '',
              caregiver_email: customer.caregiver_email || '',
            });
          } else {
            setError('No loved one found. Please create a loved one first.');
          }
        } else {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          setError(`Failed to load loved one information: ${errorData.detail || 'Unknown error'}`);
        }
      } catch (err) {
        setError('Error loading loved one information. Please try again.');
        console.error('Error loading loved one data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLovedOneData();
  }, [navigate]);

  const handleInputChange = (field: keyof LovedOneData, value: any) => {
    setLovedOne(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTimeChange = (index: number, value: string) => {
    const newTimes = [...lovedOne.check_in_times];
    newTimes[index] = value;
    setLovedOne(prev => ({
      ...prev,
      check_in_times: newTimes,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Validate required fields
      if (!lovedOne.first_name.trim() || !lovedOne.last_name.trim() || !lovedOne.phone_number.trim()) {
        setError('First name, last name, and phone number are required');
        setSaving(false);
        return;
      }

      // Basic phone number validation
      const phoneRegex = /^\+?1?\s*\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
      if (!phoneRegex.test(lovedOne.phone_number.trim())) {
        setError('Please enter a valid phone number (e.g., +1 (555) 123-4567)');
        setSaving(false);
        return;
      }

      // Reconstruct the customer name from first and last name
      const customerName = `${lovedOne.first_name.trim()} ${lovedOne.last_name.trim()}`.trim();
      
      // Prepare update data with all required fields
      const updateData: any = {
        name: customerName,
        phone_number: lovedOne.phone_number.trim(),
        caregiver_name: lovedOne.caregiver_name || '',
        caregiver_phone: lovedOne.caregiver_phone || '',
        caregiver_email: lovedOne.caregiver_email || null,
        timezone: lovedOne.timezone,
        check_in_times: lovedOne.check_in_times,
        custom_message: lovedOne.custom_message.trim() || null,
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/customers/${lovedOne.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        setSuccess('Loved one information updated successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update loved one information');
      }
    } catch (err) {
      setError('Error updating loved one information');
      console.error('Error updating loved one:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          Edit Loved One Information
        </Typography>
        
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Update your loved one's information and check-in preferences
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="firstName"
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                value={lovedOne.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="lastName"
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                value={lovedOne.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="phoneNumber"
                label="Phone Number"
                name="phoneNumber"
                autoComplete="tel"
                type="tel"
                value={lovedOne.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                disabled={saving}
                helperText="We'll send wellness check messages to this number"
                placeholder="+1 (555) 123-4567"
              />
            </Grid>

            <Grid item xs={12}>
              <TimezoneSelector
                onTimezoneChange={(timezone) => handleInputChange('timezone', timezone)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" disabled={saving}>
                <FormLabel component="legend">Check-in Frequency</FormLabel>
                <RadioGroup
                  row
                  value={lovedOne.check_in_frequency}
                  onChange={(e) => handleInputChange('check_in_frequency', parseInt(e.target.value))}
                >
                  <FormControlLabel value={1} control={<Radio />} label="Once daily" />
                  <FormControlLabel value={2} control={<Radio />} label="Twice daily" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="time1"
                label="First Check-in Time"
                type="time"
                value={lovedOne.check_in_times[0]}
                onChange={(e) => handleTimeChange(0, e.target.value)}
                disabled={saving}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {lovedOne.check_in_frequency === 2 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="time2"
                  label="Second Check-in Time"
                  type="time"
                  value={lovedOne.check_in_times[1]}
                  onChange={(e) => handleTimeChange(1, e.target.value)}
                  disabled={saving}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="customMessage"
                label="Custom Message (Optional)"
                name="customMessage"
                multiline
                rows={3}
                value={lovedOne.custom_message}
                onChange={(e) => handleInputChange('custom_message', e.target.value)}
                disabled={saving}
                helperText="Add a personal message to your wellness checks (e.g., 'I love you' or 'Remember to take your pills')"
                placeholder="I love you and hope you're having a great day!"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard')}
                  disabled={saving}
                  sx={{ minWidth: 120 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{ minWidth: 120 }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default EditLovedOne;
